import React, { useState, useEffect, useRef } from 'react';
import ComponentExportButton from './ComponentExportButton';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  Brain,
  FileText,
  TrendingUp,
  Eye,
  Clock,
  Users,
  Loader2,
  ChevronDown,
  ChevronUp,
  Check,
  X
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface MRMClassificationProps {
  opportunityId: number;
  projectId: number;
  opportunityTitle: string;
  solutionApproach: string;
  aiIntegrationOpportunities?: string;
  viewMode?: 'all' | 'business' | 'compliance';
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  onApprovalChange?: (status: 'approved' | 'rejected') => void;
}

interface MRMClassificationData {
  tier: 'Tier 1' | 'Tier 2' | 'Tier 3' | 'Non-AI';
  tierLevel: 1 | 2 | 3 | 0;
  riskLevel: 'High' | 'Moderate' | 'Low' | 'Minimal';
  requiresMRMReview: boolean;
  usesAI: boolean;
  aiComponents: {
    component: string;
    purpose: string;
    riskContribution: string;
  }[];
  classification: {
    reason: string;
    keyFactors: string[];
    impactArea: string;
    decisionInfluence: string;
  };
  validationRequirements: {
    category: string;
    requirement: string;
    frequency: string;
    responsible: string;
    criticality: 'Required' | 'Recommended' | 'Optional';
  }[];
  monitoringRequirements: {
    metric: string;
    frequency: string;
    threshold: string;
    action: string;
  }[];
  documentationRequirements: {
    document: string;
    purpose: string;
    owner: string;
    updateFrequency: string;
  }[];
  governanceRequirements: {
    activity: string;
    frequency: string;
    stakeholders: string[];
    deliverable: string;
  }[];
  escalationCriteria: string[];
}

const MRMClassification: React.FC<MRMClassificationProps> = ({
  opportunityId,
  projectId,
  opportunityTitle,
  solutionApproach,
  aiIntegrationOpportunities,
  viewMode = 'all',
  approvalStatus = 'pending',
  onApprovalChange
}) => {
  const [loading, setLoading] = useState(true);
  const [mrmData, setMrmData] = useState<MRMClassificationData | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(() => {
    if (viewMode === 'compliance') return true;
    if (viewMode === 'business') return false;
    return false;
  });
  const [currentApprovalStatus, setCurrentApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>(approvalStatus || 'pending');
  const [updatingApproval, setUpdatingApproval] = useState(false);

  useEffect(() => {
    loadMRMClassification();
  }, [opportunityId, projectId]);

  useEffect(() => {
    if (approvalStatus) {
      setCurrentApprovalStatus(approvalStatus);
    }
  }, [approvalStatus]);

  const loadMRMClassification = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/process-optimization/${projectId}/opportunities/${opportunityId}/mrm-classification`
      );
      setMrmData(response.data);
    } catch (error) {
      console.error('Failed to load MRM classification:', error);
      toast.error('Failed to load MRM classification');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalChange = async (status: 'approved' | 'rejected' | 'pending') => {
    try {
      setUpdatingApproval(true);
      await api.patch(
        `/api/process-optimization/${projectId}/opportunities/${opportunityId}/mrm-approval`,
        { status }
      );
      setCurrentApprovalStatus(status);
      if (status !== 'pending') {
        onApprovalChange?.(status);
      }
      toast.success(`MRM classification ${status === 'pending' ? 'reset to pending' : status}`);
    } catch (error) {
      console.error('Failed to update approval status:', error);
      toast.error('Failed to update approval status');
    } finally {
      setUpdatingApproval(false);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Tier 1':
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-red-500', icon: 'text-red-500' };
      case 'Tier 2':
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-orange-500', icon: 'text-orange-500' };
      case 'Tier 3':
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-yellow-500', icon: 'text-yellow-500' };
      default:
        return { bg: 'bg-white', border: 'border-gray-200', text: 'text-gray-900', badge: 'bg-green-500', icon: 'text-green-500' };
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
          <span className="text-gray-600">Analyzing Model Risk Management requirements...</span>
        </div>
      </div>
    );
  }

  if (!mrmData) {
    return null;
  }

  const colors = getTierColor(mrmData.tier);

  return (
    <div ref={exportRef} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-4 flex-1 hover:opacity-80 transition-opacity"
          >
            <div className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <Brain className={`w-5 h-5 ${colors.icon}`} />
            </div>
            <div className="text-left">
              <h3 className={`text-sm font-bold ${colors.text} flex items-center gap-2`}>
                Model Risk Management Classification
                {mrmData.requiresMRMReview && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full border border-red-300">
                    MRM Review Required
                  </span>
                )}
              </h3>
              <p className="text-xs text-gray-600">
                {mrmData.usesAI ? 'AI/ML-based automation' : 'Rules-based automation'}
              </p>
            </div>
          </button>

          <div className="flex items-center gap-3">
            <ComponentExportButton targetRef={exportRef} filename={`mrm-classification-${opportunityId}`} />
            {/* Approval Status & Buttons */}
            {currentApprovalStatus === 'pending' ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleApprovalChange('approved')}
                  disabled={updatingApproval}
                  className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                  title="Approve MRM classification"
                >
                  <Check className="w-4 h-4" />
                  Approve
                </button>
                <button
                  onClick={() => handleApprovalChange('rejected')}
                  disabled={updatingApproval}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                  title="Reject MRM classification"
                >
                  <X className="w-4 h-4" />
                  Reject
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                  currentApprovalStatus === 'approved'
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-red-100 text-red-700 border border-red-300'
                }`}>
                  {currentApprovalStatus === 'approved' ? (
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4" /> Approved
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <X className="w-4 h-4" /> Rejected
                    </span>
                  )}
                </span>
                <button
                  onClick={() => handleApprovalChange('pending')}
                  disabled={updatingApproval}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Reset
                </button>
              </div>
            )}

            <div className={`px-4 py-1.5 min-w-[52px] ${colors.badge} text-white rounded-md text-center`}>
              <div className="text-[9px] uppercase tracking-wide opacity-75 font-medium">Tier</div>
              <div className="text-xs font-bold">{mrmData.tier.replace('Tier ', '')}</div>
            </div>

            <button onClick={() => setExpanded(!expanded)} className="hover:opacity-70 transition-opacity">
              {expanded ? (
                <ChevronUp className={`w-4 h-4 ${colors.icon}`} />
              ) : (
                <ChevronDown className={`w-4 h-4 ${colors.icon}`} />
              )}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-6 pt-0 space-y-6">
          {mrmData.usesAI && mrmData.aiComponents.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                AI/ML Components Detected
              </h4>
              <div className="space-y-3">
                {mrmData.aiComponents.map((component, idx) => (
                  <div key={idx} className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <div className="font-semibold text-sm text-purple-900 mb-1">
                      {component.component}
                    </div>
                    <div className="text-xs text-gray-700 mb-1">
                      <strong>Purpose:</strong> {component.purpose}
                    </div>
                    <div className="text-xs text-gray-700">
                      <strong>Risk Contribution:</strong> {component.riskContribution}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-100 p-4">
            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Classification Rationale
            </h4>
            <div className="space-y-3">
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-1">Reason</div>
                <div className="text-sm text-gray-900">{mrmData.classification.reason}</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-1">Key Risk Factors</div>
                <ul className="space-y-1">
                  {mrmData.classification.keyFactors.map((factor, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-blue-600">•</span>
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-1">Impact Area</div>
                  <div className="text-sm text-gray-900">{mrmData.classification.impactArea}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-1">Decision Influence</div>
                  <div className="text-sm text-gray-900">{mrmData.classification.decisionInfluence}</div>
                </div>
              </div>
            </div>
          </div>

          {mrmData.validationRequirements.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Validation Requirements
              </h4>
              <div className="space-y-2">
                {mrmData.validationRequirements.map((req, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-semibold text-sm text-gray-900">{req.requirement}</div>
                      <span className={`px-2 py-1 text-xs font-bold rounded ${
                        req.criticality === 'Required' ? 'bg-red-100 text-red-700' :
                        req.criticality === 'Recommended' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {req.criticality}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-gray-600">Category:</span>{' '}
                        <span className="text-gray-900 font-medium">{req.category}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Frequency:</span>{' '}
                        <span className="text-gray-900 font-medium">{req.frequency}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Responsible:</span>{' '}
                        <span className="text-gray-900 font-medium">{req.responsible}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mrmData.monitoringRequirements.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Eye className="w-5 h-5 text-orange-600" />
                Ongoing Monitoring Requirements
              </h4>
              <div className="space-y-2">
                {mrmData.monitoringRequirements.map((mon, idx) => (
                  <div key={idx} className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                    <div className="font-semibold text-sm text-orange-900 mb-2">{mon.metric}</div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-gray-600">Frequency:</span>{' '}
                        <span className="text-gray-900 font-medium">{mon.frequency}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Threshold:</span>{' '}
                        <span className="text-gray-900 font-medium">{mon.threshold}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Action:</span>{' '}
                        <span className="text-gray-900 font-medium">{mon.action}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mrmData.documentationRequirements.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Documentation Requirements
              </h4>
              <div className="space-y-2">
                {mrmData.documentationRequirements.map((doc, idx) => (
                  <div key={idx} className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                    <div className="font-semibold text-sm text-indigo-900 mb-1">{doc.document}</div>
                    <div className="text-xs text-gray-700 mb-2">{doc.purpose}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-600">Owner:</span>{' '}
                        <span className="text-gray-900 font-medium">{doc.owner}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Update Frequency:</span>{' '}
                        <span className="text-gray-900 font-medium">{doc.updateFrequency}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mrmData.governanceRequirements.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4">
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Governance Activities
              </h4>
              <div className="space-y-2">
                {mrmData.governanceRequirements.map((gov, idx) => (
                  <div key={idx} className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <div className="font-semibold text-sm text-purple-900 mb-2">{gov.activity}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                      <div>
                        <span className="text-gray-600">Frequency:</span>{' '}
                        <span className="text-gray-900 font-medium">{gov.frequency}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Deliverable:</span>{' '}
                        <span className="text-gray-900 font-medium">{gov.deliverable}</span>
                      </div>
                    </div>
                    <div className="text-xs">
                      <span className="text-gray-600">Stakeholders:</span>{' '}
                      <span className="text-gray-900 font-medium">{gov.stakeholders.join(', ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mrmData.escalationCriteria.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Escalation Criteria
              </h4>
              <p className="text-xs text-red-700 mb-3">
                Escalate to MRM team immediately if any of the following occur:
              </p>
              <ul className="space-y-2">
                {mrmData.escalationCriteria.map((criteria, idx) => (
                  <li key={idx} className="text-sm text-red-800 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {criteria}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MRMClassification;
